import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner } from "../../../_components/Contacts/ContactShared";
import { describeBulkOutcome } from "../../../_components/Shared/BulkApproval";
import FinanceDocList from "../../../_components/Finance/FinanceDocList";
import {
    getFinanceApprovalList,
    isFinalApprovalDecision,
} from "../../../_services/approval.service";
import {
    FiClock,
    FiUser,
    FiDollarSign,
    FiHome,
    FiFileText
} from "react-icons/fi";
import { T, fmtCurrency, ModuleApprovalActions } from "../../../_components/Sales/SalesShared";
import { DocStatusBadge as FinanceStatusBadge } from "../../../_components/Finance/FinanceShared";

const APPROVAL_STATUSES = ["All", "Pending for Approval", "Approved", "Rejected"];

const FINANCE_SEARCH_OPTIONS = [
    { key: "all", label: "All" },
    { key: "id", label: "Doc No." },
    { key: "date", label: "Date" },
    { key: "party", label: "Company Name" },
    { key: "contactPerson", label: "Contact Person" }
];

const FINANCE_TYPE_META = {
    purchase_invoice: {
        label: "Purchase Invoice",
        basePath: (item) => item.piType === "Direct"
            ? "/finance/direct-purchase-invoices"
            : "/finance/indirect-purchase-invoices",
    },
    payment_voucher: {
        label: "Payment Voucher",
        basePath: () => "/finance/payment-vouchers",
    },
    purchase_credit_note: {
        label: "Purchase Credit Note",
        basePath: () => "/finance/purchase-credit-notes",
    },
    purchase_debit_note: {
        label: "Purchase Debit Note",
        basePath: () => "/finance/purchase-debit-notes",
    },
    sales_credit_note: {
        label: "Sales Credit Note",
        basePath: () => "/finance/credit-notes",
    },
    sales_debit_note: {
        label: "Sales Debit Note",
        basePath: () => "/finance/debit-notes",
    },
};

export default function FinanceApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const bannerTimerRef = useRef(null);

    const showBanner = useCallback((type, msg, duration = 4000) => {
        clearTimeout(bannerTimerRef.current);
        setBanner({ type, msg });
        bannerTimerRef.current = setTimeout(
            () => setBanner({ type: "", msg: "" }),
            duration
        );
    }, []);

    useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

    useEffect(() => {
        if (!searchParams.get("status")) {
            setSearchParams(
                {
                    status: "Pending for Approval",
                    page: "1"
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFinanceApprovals = useCallback(
        async (qp, options = {}) => {
            void refreshKey;
            const result = await getFinanceApprovalList(qp, options);
            return {
                ...result,
                data: (result.data || []).map((item) => {
                    const meta = FINANCE_TYPE_META[item.typeKey] || {};
                    const invoiceLabel = item.typeKey === "purchase_invoice"
                        ? `${item.piType || "Indirect"} Purchase Invoice`
                        : null;
                    return {
                        ...item,
                        _docType: invoiceLabel || meta.label || item.typeKey,
                        _basePath: meta.basePath ? meta.basePath(item) : "/finance",
                        _typeKey: item.typeKey,
                    };
                }),
            };
        },
        [refreshKey]
    );

    const handleDecision = useCallback((item, onClose, result, { action }) => {
        onClose();
        setRefreshKey((current) => current + 1);
        const finalApproval = action === "approve" && isFinalApprovalDecision(result);
        showBanner(
            "success",
            action === "reject"
                ? `Document ${item.id} rejected successfully.`
                : finalApproval
                    ? `Document ${item.id} approved successfully.`
                    : `Approval for document ${item.id} recorded. Pending the next approval level.`
        );
    }, [showBanner]);

    const handleBulkComplete = useCallback((outcome) => {
        setRefreshKey((current) => current + 1);
        const { type, msg } = describeBulkOutcome(outcome);
        showBanner(type, msg, outcome.failed.length ? 15000 : 4000);
    }, [showBanner]);

    const renderFinancePreviewActions = useCallback(
        (item, onClose, onView) => {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                    {item.status === "Pending for Approval" && (item.canApprove || item.canReject) && (
                        <ModuleApprovalActions
                            block
                            docType={item._docType}
                            key={`${item.recordType}:${item.dbId}`}
                            recordType={item.recordType}
                            recordId={item.dbId}
                            recordLabel={item.id}
                            currentLevel={item.currentLevel}
                            canApprove={item.canApprove}
                            canReject={item.canReject}
                            onDecision={(result, decision) => handleDecision(item, onClose, result, decision)}
                            onError={(error) => showBanner("danger", error.message || `Failed to process document ${item.id}.`)}
                        />
                    )}

                    <button
                        className="cl-preview-btn-primary"
                        onClick={() => onView(`${item._basePath}/${item.id}?fromApprovals=true`)}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "12px 16px",
                            fontSize: 14
                        }}
                    >
                        <FiFileText size={16} /> View Document Details
                    </button>
                </div>
            );
        },
        [handleDecision, showBanner]
    );

    const financeColumns = useMemo(
        () => [
            {
                label: "Doc. No.",
                key: "id",
                width: 110,
                render: (v) => (
                    <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Type",
                key: "_docType",
                width: 160,
                render: (v) => (
                    <span style={{ fontWeight: 600, color: T.accent, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Date",
                key: "date",
                width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>
            },
            {
                label: "Company Name",
                key: "party",
                width: 200,
                render: (v) => (
                    <span
                        style={{
                            fontWeight: 600,
                            display: "inline-block",
                            maxWidth: 190,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {v}
                    </span>
                )
            },
            {
                label: "Amount",
                key: "grandTotal",
                render: (v, row) => (
                    <span style={{ fontWeight: 700, color: T.textPrimary }}>
                        {fmtCurrency(v ?? row.payAmount, row.currency)}
                    </span>
                )
            },
            { label: "Status", key: "status", render: (v) => <FinanceStatusBadge value={v} /> }
        ],
        []
    );

    const financePreviewFields = useMemo(
        () => [
            { icon: FiFileText, label: "Document Type", key: "_docType" },
            { icon: FiClock, label: "Date", key: "date" },
            { icon: FiHome, label: "Company Name", key: "party" },
            { icon: FiUser, label: "Contact Person", key: "contactPerson" },
            { icon: FiDollarSign, label: "Amount", key: "grandTotal", render: (v, row) => fmtCurrency(v ?? row.payAmount, row.currency) }
        ],
        []
    );

    return (
        <div style={{ padding: "0 20px" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0, fontSize: 24 }}>
                    Finance & Accounting Approvals
                </h4>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "4px 0 0" }}>
                    Review and process finance documents: Purchase Invoices, Payment Vouchers, Debit & Credit Notes.
                </p>
            </div>

            <FinanceDocList
                onBulkComplete={handleBulkComplete}
                docType="Finance Document"
                basePath="/accounting"
                fetchList={getFinanceApprovals}
                statuses={APPROVAL_STATUSES}
                customColumns={financeColumns}
                renderPreviewActions={renderFinancePreviewActions}
                hideCreate={true}
                hideExport={true}
                searchOptions={FINANCE_SEARCH_OPTIONS}
                previewFields={financePreviewFields}
            />
        </div>
    );
}